/*
  Darstellung für den Schichtplan V2.
  Bündelt Schichtanzeige, Farbzuteilung und Farben.
*/

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Popover, Typography } from "@mui/material";
// Dasselbe Symbol wie in /settings fuer "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { IconChevronDown } from "../../../../design-system/icons";
import ShiftplanBusyDots from "../ui/ShiftplanBusyDots";
import { normalizeApiError } from "../../api/errors";
import { SHIFT_DISPLAY_MODES, normalizeShiftDisplayMode } from "../../domain/model/shiftDisplay";

// Abteilungen/Schichten sind kategoriale Daten – darum die Kategorie-Palette des
// Design Systems (--ds-color-category-01..10) statt einer eigenen Farbliste.
// Bereits gespeicherte Farben bleiben unverändert, das gilt nur für neue Auswahl.
const DEFAULT_COLOR = "#1C9A9A";
const COLOR_PRESETS = Object.freeze([
  "#D64545",
  "#2E9B59",
  "#3478D4",
  "#E0B400",
  "#D94F9D",
  "#24272B",
  "#E97B24",
  "#7B61C9",
  "#1C9A9A",
  "#8A6248",
]);
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const SHIFT_DISPLAY_OPTIONS = Object.freeze([
  {
    value: SHIFT_DISPLAY_MODES.TIME_DEPARTMENT,
    title: "Abteilung",
    subtitle: "Zeit und Abteilungsname im Slot",
    icon: <IconDepartment fontSize="small" />,
  },
  {
    value: SHIFT_DISPLAY_MODES.TIME_SHIFT,
    title: "Schicht",
    subtitle: "Zeit und Schichtname im Slot",
    icon: <WorkOutlineOutlinedIcon fontSize="small" />,
  },
]);

const COLOR_SOURCE_OPTIONS = Object.freeze([
  {
    value: "department",
    title: "Abteilungen",
    subtitle: "Slots in Abteilungsfarbe",
    icon: <IconDepartment fontSize="small" />,
  },
  {
    value: "shift",
    title: "Schichten",
    subtitle: "Slots in Schichtfarbe",
    icon: <WorkOutlineOutlinedIcon fontSize="small" />,
  },
  {
    value: "user",
    title: "Mitarbeitende",
    subtitle: "Slots pro Person eingefärbt",
    icon: <PersonOutlineIcon fontSize="small" />,
  },
]);

const COLOR_SOURCE_VALUES = new Set(COLOR_SOURCE_OPTIONS.map((option) => option.value));

function asText(value) {
  return String(value || "").trim();
}

function normalizeHexColor(value, fallback = DEFAULT_COLOR) {
  const color = asText(value);
  return HEX_COLOR_PATTERN.test(color) ? color.toUpperCase() : fallback;
}

function normalizeColorSource(value) {
  return COLOR_SOURCE_VALUES.has(value) ? value : "department";
}

function baseShiftId(value) {
  return asText(value).split(":")[0] || asText(value);
}

function colorItemKey(type, id) {
  return `${type}:${id}`;
}

function presetColorForIndex(index) {
  return COLOR_PRESETS[index % COLOR_PRESETS.length];
}

function buildDepartmentItems(departments) {
  return (Array.isArray(departments) ? departments : [])
    .map((department, index) => {
      const id = asText(department?.id ?? department?.DepartmentID ?? department?.name);
      const name = asText(department?.name ?? department?.label);
      if (!id || !name) return null;

      return {
        id,
        title: name,
        meta: asText(department?.shortName ?? department?.short_name ?? department?.shorthand_symbol),
        color: normalizeHexColor(department?.color, normalizeHexColor(department?.color_hex, presetColorForIndex(index))),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.title.localeCompare(right.title, "de", { sensitivity: "base" }));
}

function buildShiftItems(shiftTemplates) {
  const byShiftId = new Map();

  (Array.isArray(shiftTemplates) ? shiftTemplates : []).forEach((template, index) => {
    const id = baseShiftId(template?.id);
    const title = asText(template?.name);
    if (!id || !title) return;

    const current = byShiftId.get(id) || {
      id,
      title,
      shortName: asText(template?.shortName),
      timeLabel: asText(template?.timeLabel),
      departments: new Set(),
      color: normalizeHexColor(template?.color, presetColorForIndex(index)),
    };

    const department = asText(template?.department);
    if (department) current.departments.add(department);
    if (template?.color) current.color = normalizeHexColor(template.color, current.color);
    byShiftId.set(id, current);
  });

  return Array.from(byShiftId.values())
    .map((item) => {
      const departments = Array.from(item.departments);
      const departmentMeta =
        departments.length > 1 ? `${departments.length} Abteilungen` : departments[0] || "Keine Abteilung";
      return {
        id: item.id,
        title: item.title,
        meta: [item.shortName, item.timeLabel, departmentMeta].filter(Boolean).join(" · "),
        color: item.color,
      };
    })
    .sort((left, right) => left.title.localeCompare(right.title, "de", { sensitivity: "base" }));
}

function buildEmployeeItems(employees) {
  return (Array.isArray(employees) ? employees : [])
    .map((employee, index) => {
      const id = asText(employee?.id);
      const title = asText(employee?.name);
      if (!id || !title) return null;
      return {
        id,
        title,
        meta: "Automatisch",
        color: normalizeHexColor(employee?.color, presetColorForIndex(index)),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.title.localeCompare(right.title, "de", { sensitivity: "base" }));
}

function AppearanceOptionButton({ option, active, disabled, saving, onSelect }) {
  return (
    <button
      type="button"
      className={`spv2-appearance-option${active ? " is-active" : ""}`}
      disabled={disabled}
      aria-pressed={active}
      onClick={() => onSelect(option.value)}
    >
      <span className="spv2-appearance-option-icon">{option.icon}</span>
      <span className="spv2-appearance-option-copy">
        <Typography component="span" className="spv2-appearance-option-title">
          {option.title}
        </Typography>
        <Typography component="span" className="spv2-appearance-option-subtitle">
          {option.subtitle}
        </Typography>
      </span>
      <span className="spv2-appearance-option-state" aria-hidden="true">
        {saving && active ? <ShiftplanBusyDots /> : null}
      </span>
    </button>
  );
}

// Wird auch von der zentralen Typenverwaltung unter /settings verwendet, damit
// dieselbe Farbauswahl im Schichtplan und in den Unternehmenseinstellungen gilt.
export function ColorSwatchControl({ type, item, color, saving = false, onColorChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [customColor, setCustomColor] = useState(color);
  const isOpen = Boolean(anchorEl);
  const normalizedColor = normalizeHexColor(color, DEFAULT_COLOR);
  const normalizedCustom = normalizeHexColor(customColor, normalizedColor);
  const isCustomChanged = normalizedCustom !== normalizedColor;

  const openPopover = (event) => {
    setCustomColor(normalizedColor);
    setAnchorEl(event.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);
  const applyColor = (nextColor) => {
    closePopover();
    onColorChange(type, item, nextColor);
  };

  return (
    <>
      <button
        type="button"
        className={`spv2-color-swatch${isOpen ? " is-open" : ""}${saving ? " is-saving" : ""}`}
        aria-label={`Farbe für ${item.title} ändern`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={saving}
        onClick={openPopover}
      >
        <span className="spv2-color-swatch-dot" style={{ "--spv2-color-editor-color": normalizedColor }} aria-hidden="true" />
        {saving ? (
          <ShiftplanBusyDots />
        ) : (
          <IconChevronDown className="spv2-color-swatch-caret" fontSize="small" />
        )}
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 3400 }}
        PaperProps={{ className: "spv2-color-popover-paper" }}
      >
        <Box className="spv2-color-popover">
          <Box className="spv2-color-popover-grid" role="listbox" aria-label={`Farbe für ${item.title}`}>
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                role="option"
                aria-selected={preset === normalizedColor}
                className={`spv2-color-popover-preset${preset === normalizedColor ? " is-active" : ""}`}
                style={{ "--spv2-color-editor-color": preset }}
                aria-label={`${item.title} auf ${preset} setzen`}
                onClick={() => applyColor(preset)}
              />
            ))}
          </Box>

          <Box className="spv2-color-popover-custom-section">
            <label className="spv2-color-popover-custom">
              <span className="spv2-color-popover-custom-dot" style={{ "--spv2-color-editor-color": normalizedCustom }} aria-hidden="true" />
              <span className="spv2-color-popover-custom-label">Eigene Farbe</span>
              <span className="spv2-color-popover-custom-value">{normalizedCustom}</span>
              <input
                type="color"
                value={normalizedCustom}
                aria-label={`${item.title} Farbe wählen`}
                onChange={(event) => setCustomColor(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="spv2-color-popover-apply"
              disabled={!isCustomChanged}
              onClick={() => applyColor(normalizedCustom)}
            >
              Übernehmen
            </button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

function ColorPickerRow({ type, item, color, saving = false, readOnly = false, onColorChange }) {
  return (
    <Box className={`spv2-color-row${readOnly ? " is-readonly" : ""}`}>
      <span className="spv2-color-row-copy">
        <Typography component="span" className="spv2-color-row-title">
          {item.title}
        </Typography>
        {item.meta ? (
          <Typography component="span" className="spv2-color-row-meta">
            {item.meta}
          </Typography>
        ) : null}
      </span>

      {readOnly ? (
        <span className="spv2-color-row-auto">
          <span className="spv2-color-row-auto-dot" style={{ "--spv2-color-editor-color": color }} aria-hidden="true" />
          Auto
        </span>
      ) : (
        <ColorSwatchControl type={type} item={item} color={color} saving={saving} onColorChange={onColorChange} />
      )}
    </Box>
  );
}

function ColorGroup({ title, items, type, readOnly = false, emptyText, savingColorByKey, getDisplayedColor, onColorChange }) {
  return (
    <Box className="spv2-color-group">
      <Typography component="span" className="spv2-color-group-title">
        {title}
      </Typography>
      {items.length ? (
        <Box className="spv2-color-group-list" aria-label={`${title} Farben`}>
          {items.map((item) => {
            const key = colorItemKey(type, item.id);
            return (
              <ColorPickerRow
                key={key}
                type={type}
                item={item}
                color={getDisplayedColor(type, item)}
                saving={Boolean(savingColorByKey[key])}
                readOnly={readOnly}
                onColorChange={onColorChange}
              />
            );
          })}
        </Box>
      ) : (
        <Box className="spv2-color-editor-empty">{emptyText}</Box>
      )}
    </Box>
  );
}

export function AppearanceSettingsContent({
  active = true,
  shiftDisplayMode = SHIFT_DISPLAY_MODES.TIME_DEPARTMENT,
  displayCalendar = "department",
  departments = [],
  shiftTemplates = [],
  employees = [],
  onShiftDisplayModeChange,
  onDisplayCalendarChange,
  onDepartmentColorChange,
  onShiftColorChange,
}) {
  const savedShiftDisplayMode = normalizeShiftDisplayMode(shiftDisplayMode);
  const savedColorSource = normalizeColorSource(displayCalendar);
  const [localShiftDisplayMode, setLocalShiftDisplayMode] = useState(savedShiftDisplayMode);
  const [localColorSource, setLocalColorSource] = useState(savedColorSource);
  const [isSavingShiftDisplay, setIsSavingShiftDisplay] = useState(false);
  const [isSavingColorSource, setIsSavingColorSource] = useState(false);
  const [savingColorByKey, setSavingColorByKey] = useState({});
  const [draftColorByKey, setDraftColorByKey] = useState({});
  const [error, setError] = useState("");

  const departmentItems = useMemo(() => buildDepartmentItems(departments), [departments]);
  const shiftItems = useMemo(() => buildShiftItems(shiftTemplates), [shiftTemplates]);
  const employeeItems = useMemo(() => buildEmployeeItems(employees), [employees]);

  useEffect(() => {
    if (!active) return;
    setLocalShiftDisplayMode(savedShiftDisplayMode);
    setLocalColorSource(savedColorSource);
    setError("");
    setIsSavingShiftDisplay(false);
    setIsSavingColorSource(false);
    setSavingColorByKey({});
    setDraftColorByKey({});
  }, [active, savedColorSource, savedShiftDisplayMode]);

  const getDisplayedColor = (type, item) => {
    const key = colorItemKey(type, item.id);
    return normalizeHexColor(draftColorByKey[key], item.color);
  };

  const slotColorGroup = useMemo(() => {
    if (localColorSource === "shift") {
      return { title: "Schichtfarben", type: "shift", items: shiftItems, readOnly: false, emptyText: "Keine Schichten gefunden." };
    }

    if (localColorSource === "user") {
      return { title: "Mitarbeitende", type: "user", items: employeeItems, readOnly: true, emptyText: "Keine Mitarbeitenden gefunden." };
    }

    return { title: "Abteilungsfarben", type: "department", items: departmentItems, readOnly: false, emptyText: "Keine Abteilungen gefunden." };
  }, [departmentItems, employeeItems, localColorSource, shiftItems]);

  const handleShiftDisplaySelect = async (nextValue) => {
    const normalizedValue = normalizeShiftDisplayMode(nextValue);
    if (normalizedValue === localShiftDisplayMode || isSavingShiftDisplay) return;

    const previousValue = localShiftDisplayMode;
    setError("");
    setLocalShiftDisplayMode(normalizedValue);

    if (normalizedValue === savedShiftDisplayMode || !onShiftDisplayModeChange) return;

    setIsSavingShiftDisplay(true);
    try {
      await onShiftDisplayModeChange(normalizedValue);
    } catch (changeError) {
      setLocalShiftDisplayMode(previousValue);
      setError(normalizeApiError(changeError).message || "Die Schichtanzeige konnte nicht gespeichert werden.");
    } finally {
      setIsSavingShiftDisplay(false);
    }
  };

  const handleColorSourceSelect = async (nextValue) => {
    const normalizedValue = normalizeColorSource(nextValue);
    if (normalizedValue === localColorSource || isSavingColorSource) return;

    const previousValue = localColorSource;
    setError("");
    setLocalColorSource(normalizedValue);

    if (normalizedValue === savedColorSource || !onDisplayCalendarChange) return;

    setIsSavingColorSource(true);
    try {
      await onDisplayCalendarChange(normalizedValue);
    } catch (changeError) {
      setLocalColorSource(previousValue);
      setError(normalizeApiError(changeError).message || "Die Farbzuteilung konnte nicht gespeichert werden.");
    } finally {
      setIsSavingColorSource(false);
    }
  };

  const setColorSaving = (key, nextValue) => {
    setSavingColorByKey((current) => {
      const next = { ...current };
      if (nextValue) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const handleColorChange = async (type, item, nextColor) => {
    const normalizedColor = normalizeHexColor(nextColor, "");
    if (!normalizedColor || type === "user") return;

    const key = colorItemKey(type, item.id);
    const previousColor = getDisplayedColor(type, item);
    const saveColor = type === "department" ? onDepartmentColorChange : type === "shift" ? onShiftColorChange : null;

    if (!saveColor || normalizedColor === previousColor || savingColorByKey[key]) return;

    setError("");
    setDraftColorByKey((current) => ({ ...current, [key]: normalizedColor }));
    setColorSaving(key, true);

    try {
      await saveColor({ id: item.id, colorHex: normalizedColor });
    } catch (changeError) {
      setDraftColorByKey((current) => ({ ...current, [key]: previousColor }));
      setError(normalizeApiError(changeError).message || "Die Farbe konnte nicht gespeichert werden.");
    } finally {
      setColorSaving(key, false);
    }
  };

  return (
    <Box className="spv2-appearance-panel">
      <Box className="spv2-settings-section-intro">
        <Box className="spv2-settings-section-head">
          <Box className="spv2-settings-section-copy">
            <Typography className="spv2-settings-section-title">Darstellung</Typography>
          </Box>
        </Box>
        <Typography className="spv2-settings-section-subtitle">
          Bestimmt, wie Schichten, Absenzen und Ferien im Schichtplan aussehen.
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" className="spv2-color-panel-alert">
          {error}
        </Alert>
      ) : null}

      <Box className="spv2-appearance-section">
        <Typography component="span" className="spv2-appearance-section-label">
          Schichtanzeige
        </Typography>
        <Box className="spv2-appearance-options is-two" aria-label="Schichtanzeige">
          {SHIFT_DISPLAY_OPTIONS.map((option) => (
            <AppearanceOptionButton
              key={option.value}
              option={option}
              active={localShiftDisplayMode === option.value}
              disabled={isSavingShiftDisplay}
              saving={isSavingShiftDisplay}
              onSelect={handleShiftDisplaySelect}
            />
          ))}
        </Box>
      </Box>

      <Box className="spv2-appearance-section">
        <Typography component="span" className="spv2-appearance-section-label">
          Farbzuteilung
        </Typography>
        <Box className="spv2-appearance-options is-three" aria-label="Farbzuteilung">
          {COLOR_SOURCE_OPTIONS.map((option) => (
            <AppearanceOptionButton
              key={option.value}
              option={option}
              active={localColorSource === option.value}
              disabled={isSavingColorSource}
              saving={isSavingColorSource}
              onSelect={handleColorSourceSelect}
            />
          ))}
        </Box>
      </Box>

      <Box className="spv2-appearance-color-card">
        {/*
          Absenz- und Ferienfarben stehen zentral unter /settings bei den
          Typen. Hier bleibt nur, worauf die Farbzuteilung oben tatsächlich
          wirkt.
        */}
        <ColorGroup
          title={slotColorGroup.title}
          items={slotColorGroup.items}
          type={slotColorGroup.type}
          readOnly={slotColorGroup.readOnly}
          emptyText={slotColorGroup.emptyText}
          savingColorByKey={savingColorByKey}
          getDisplayedColor={getDisplayedColor}
          onColorChange={handleColorChange}
        />
      </Box>
    </Box>
  );
}
